/**
 * Shared denial-cases table -- the same 10-column row used on the /denials
 * list page and inside the Personal / Shared Queue tabs on the Dashboard.
 *
 * Caller supplies the rows + a couple of action callbacks; the component
 * owns rendering, status pills, and Open/Run + Download buttons.
 *
 * Columns:
 *   1. External ID
 *   2. Payer / Claim (two lines)
 *   3. Denial Type (archetype)
 *   4. Status
 *   5. Expected Recovery
 *   6. Confidence
 *   7. Appeal Status
 *   8. Last Run
 *   9. Open / Run (button -- "Run" when status='new' and no execution, else "Open")
 *  10. Download (zip of original uploaded documents; disabled when HasDocuments=false)
 */
import React from 'react';
import { Link } from 'react-router-dom';
import './DenialsListPage.css';

const STATUS_LABELS = {
  new: 'New',
  processing: 'Processing',
  pending_review: 'Pending Review',
  appealed: 'Appealed',
  closed: 'Closed',
};

const APPEAL_STATUS_LABELS = {
  draft: 'Draft',
  ready_to_submit: 'Ready to Submit',
  pending_review: 'Pending Review',
  submitted: 'Submitted',
  submitted_fallback: 'Submitted (fallback)',
  rejected: 'Rejected',
  won: 'Won',
  lost: 'Lost',
};

const ARCHETYPE_LABELS = {
  medical_necessity:        'Medical Necessity',
  coding_bundling:          'Coding & Bundling',
  coding_ncci_mue:          'Coding (NCCI/MUE)',
  cob_crossover:            'COB Crossover',
  cob_msp:                  'COB / MSP',
  drg_downgrade:            'DRG Downgrade',
  dx_proc_inconsistency:    'Dx/Proc Inconsistency',
  prior_auth:               'Prior Authorization',
  eligibility_coverage:     'Eligibility / Coverage',
  duplicate_timely:         'Duplicate / Timely',
  documentation:            'Documentation',
  payment_underpayment:     'Payment / Underpayment',
  credentialing_network:    'Credentialing / Network',
  intake_identity:          'Intake / Identity',
  frontend_rejection:       'Front-end Rejection',
};

const NO_VALUE = '—';

function formatCurrency(v) {
  if (v === null || v === undefined || v === '') return NO_VALUE;
  const n = Number(v);
  if (Number.isNaN(n)) return NO_VALUE;
  return n.toLocaleString(undefined, {
    style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
}

function formatPct(v) {
  if (v === null || v === undefined || v === '') return NO_VALUE;
  const n = Number(v);
  if (Number.isNaN(n)) return NO_VALUE;
  const pct = n <= 1 ? n * 100 : n;
  return `${pct.toFixed(0)}%`;
}

function formatDate(s) {
  if (!s) return NO_VALUE;
  try {
    return new Date(s).toLocaleString();
  } catch (_e) {
    return s;
  }
}

function formatRelative(s) {
  if (!s) return NO_VALUE;
  try {
    const then = new Date(s).getTime();
    const now = Date.now();
    const diff = Math.max(0, now - then);
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins} min ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs} hr ago`;
    const days = Math.floor(hrs / 24);
    return `${days} day${days === 1 ? '' : 's'} ago`;
  } catch (_e) {
    return s;
  }
}

function archetypeLabel(value) {
  if (!value) return NO_VALUE;
  return ARCHETYPE_LABELS[value] || value;
}

function isRunnable(status, executionId) {
  // "Run" when the case has never executed; otherwise "Open".
  const s = String(status || '').toLowerCase();
  return !executionId && (s === 'new' || s === '');
}

export default function DenialsCasesTable({
  items,
  loading,
  error,
  emptyMessage,
  onOpen,
  onDownload,
  downloadingId,
}) {
  const rows = Array.isArray(items) ? items : [];

  return (
    <div className="denials-table-wrap">
      <table className="denials-table">
        <thead>
          <tr>
            <th>External ID</th>
            <th>Payer / Claim</th>
            <th>Denial Type</th>
            <th>Status</th>
            <th title="Engine-calculated expected recovery from the latest appeal.">Expected Recovery</th>
            <th title="Confidence in the latest appeal (averaged across plan, finding, engine reasoning).">Confidence</th>
            <th title="Routing decision from the latest appeal.">Appeal Status</th>
            <th>Last Run</th>
            <th />
            <th />
          </tr>
        </thead>
        <tbody>
          {loading && (
            <tr><td colSpan={10} className="denials-empty">Loading…</td></tr>
          )}
          {!loading && error && rows.length === 0 && (
            <tr><td colSpan={10} className="denials-empty">{error}</td></tr>
          )}
          {!loading && !error && rows.length === 0 && (
            <tr><td colSpan={10} className="denials-empty">
              {emptyMessage || 'No denial cases.'}
            </td></tr>
          )}
          {!loading && rows.map((row) => {
            const caseId       = row.CaseId || row.caseId;
            const externalId   = row.ExternalId || row.externalId || caseId;
            const caseStatus   = (row.Status || row.status || 'new').toLowerCase();
            const executionId  = row.ExecutionId || row.executionId || null;
            const appealStatus = (row.LatestAppealStatus || '').toLowerCase();
            const lastRunAt    = row.LatestAppealCreatedAt
                                 || row.UpdatedAt || row.updatedAt;
            const hr           = row.LatestHumanReviewRequired;
            const hasDocs      = Boolean(row.HasDocuments);
            const downloading  = downloadingId === caseId;
            const openLabel    = isRunnable(caseStatus, executionId) ? 'Run' : 'Open';
            return (
              <tr key={caseId}>
                <td className="denials-cell-ext">
                  <div>
                    {isRunnable(caseStatus, executionId)
                      ? <Link to={`/intake/${caseId}`}>{externalId}</Link>
                      : externalId}
                  </div>
                  {row.AppealCount > 0 && (
                    <div className="denials-cell-sub">
                      {row.AppealCount} appeal{row.AppealCount === 1 ? '' : 's'}
                    </div>
                  )}
                </td>
                <td>
                  <div>{row.PayerName || row.payerName || row.PayerId || NO_VALUE}</div>
                  <div className="denials-cell-sub">
                    {row.ClaimNumber || row.claimNumber || NO_VALUE}
                  </div>
                </td>
                <td>{archetypeLabel(row.Archetype || row.archetype)}</td>
                <td>
                  <span className={`denials-status denials-status--${caseStatus}`}>
                    {STATUS_LABELS[caseStatus] || caseStatus}
                  </span>
                </td>
                <td className="denials-cell-num">
                  {row.LatestExpectedRecovery != null
                    ? <strong>{formatCurrency(row.LatestExpectedRecovery)}</strong>
                    : <span className="denials-cell-muted">{NO_VALUE}</span>}
                </td>
                <td className="denials-cell-num">
                  {row.LatestAppealConfidence != null
                    ? formatPct(row.LatestAppealConfidence)
                    : <span className="denials-cell-muted">{NO_VALUE}</span>}
                </td>
                <td>
                  {appealStatus
                    ? (
                        <span className={`denials-status denials-status--appeal-${appealStatus.replace(/_/g, '-')}`}>
                          {APPEAL_STATUS_LABELS[appealStatus] || appealStatus}
                        </span>
                      )
                    : <span className="denials-cell-muted">no appeal yet</span>}
                  {hr === true && (
                    <span className="denials-flag denials-flag--review">HITL</span>
                  )}
                </td>
                <td title={formatDate(lastRunAt)}>
                  {formatRelative(lastRunAt)}
                </td>
                <td className="denials-row-actions">
                  <button
                    type="button"
                    className="denials-btn denials-btn--primary"
                    onClick={() => onOpen && onOpen(caseId, { status: caseStatus, executionId })}
                  >
                    {openLabel}
                  </button>
                </td>
                <td className="denials-row-actions">
                  <button
                    type="button"
                    className="denials-link-btn"
                    onClick={() => onDownload && onDownload(caseId, { externalId, hasDocuments: hasDocs })}
                    disabled={!hasDocs || downloading}
                    title={hasDocs
                      ? 'Download all uploaded documents for this case (.zip)'
                      : 'No uploaded documents on this case'}
                  >
                    {downloading ? 'Downloading…' : 'Download'}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
